import React, { useCallback, useEffect, useRef, useState } from 'react';
import { pdfjs } from './pdfjs-setup';
import { FaChevronLeft } from '@react-icons/all-files/fa/FaChevronLeft';
import { FaChevronRight } from '@react-icons/all-files/fa/FaChevronRight';
import { FaEye } from '@react-icons/all-files/fa/FaEye';
import { FaSpinner } from '@react-icons/all-files/fa/FaSpinner';
import ProfaseModal from '../commons/ProfaseModal';
import ArchivoViewerTitle from './ArchivoViewerTitle';
import { fetchProfaseArchivoContenido, type IProfaseArchivo } from './profase-archivo-api';
import './pdf-viewer-modal.css';

interface PdfViewerModalProps {
  open: boolean;
  archivo: IProfaseArchivo | null;
  onClose: () => void;
  fetchContent?: () => Promise<Blob>;
}

function isRenderingCancelled(error: unknown): boolean {
  return error != null
    && typeof error === 'object'
    && 'name' in error
    && (error as { name?: string }).name === 'RenderingCancelledException';
}

export default function PdfViewerModal({ open, archivo, onClose, fetchContent }: PdfViewerModalProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const pdfDocRef = useRef<pdfjs.PDFDocumentProxy | null>(null);
  const renderTaskRef = useRef<pdfjs.RenderTask | null>(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pageNum, setPageNum] = useState(1);
  const [numPages, setNumPages] = useState(0);

  const destroyPdf = useCallback(() => {
    renderTaskRef.current?.cancel();
    renderTaskRef.current = null;
    if (pdfDocRef.current) {
      void pdfDocRef.current.cleanup();
      pdfDocRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!open || (!fetchContent && archivo?.id == null)) {
      destroyPdf();
      setPageNum(1);
      setNumPages(0);
      setError(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    destroyPdf();
    setLoading(true);
    setError(null);
    setPageNum(1);
    setNumPages(0);

    void (async () => {
      try {
        const { blob } = fetchContent
          ? { blob: await fetchContent() }
          : await fetchProfaseArchivoContenido(archivo!.id!, archivo!.nombre);
        if (cancelled) {
          return;
        }

        const doc = await pdfjs.getDocument({ data: await blob.arrayBuffer() }).promise;
        if (cancelled) {
          void doc.cleanup();
          return;
        }

        pdfDocRef.current = doc;
        setNumPages(doc.numPages);
        setPageNum(1);
      } catch {
        if (!cancelled) {
          setError('No se pudo cargar el PDF.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
      destroyPdf();
    };
  }, [open, archivo?.id, archivo?.nombre, destroyPdf, fetchContent]);

  useEffect(() => {
    const doc = pdfDocRef.current;
    const canvas = canvasRef.current;
    if (!open || !doc || !canvas || loading || error) {
      return;
    }

    let cancelled = false;

    void (async () => {
      renderTaskRef.current?.cancel();
      try {
        const page = await doc.getPage(pageNum);
        if (cancelled) {
          return;
        }

        const parentWidth = canvas.parentElement?.clientWidth ?? 900;
        const baseViewport = page.getViewport({ scale: 1 });
        const scale = Math.min(2, Math.max(0.5, (parentWidth - 48) / baseViewport.width));
        const viewport = page.getViewport({ scale });

        canvas.width = viewport.width;
        canvas.height = viewport.height;

        const task = page.render({ canvas, viewport });
        renderTaskRef.current = task;
        await task.promise;
      } catch (renderError) {
        if (!cancelled && !isRenderingCancelled(renderError)) {
          setError('No se pudo renderizar la página.');
        }
      }
    })();

    return () => {
      cancelled = true;
      renderTaskRef.current?.cancel();
    };
  }, [open, loading, error, pageNum, numPages]);

  const goPrev = () => {
    setPageNum(current => Math.max(1, current - 1));
  };

  const goNext = () => {
    setPageNum(current => Math.min(numPages, current + 1));
  };

  return (
    <ProfaseModal
      open={open}
      titleId="pdf-viewer-modal-title"
      size="xlarge"
      scroll
      dialogClassName="pdf-viewer-modal"
      onBackdropMouseDown={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div className="pdf-viewer-modal__header">
        <h2 id="pdf-viewer-modal-title" className="profase-modal-title m-0 pdf-viewer-modal__title">
          <FaEye style={{ marginRight: 8, verticalAlign: '-2px' }} />
          <ArchivoViewerTitle archivo={archivo} fallback="Vista previa PDF" />
        </h2>
        <button type="button" className="profase-form-btn-secondary btn-sm" onClick={onClose}>
          Cerrar
        </button>
      </div>

      {numPages > 1 && (
        <div className="pdf-viewer-modal__pager">
          <button
            type="button"
            className="pdf-viewer-modal__pager-btn"
            disabled={loading || pageNum <= 1}
            onClick={goPrev}
            aria-label="Página anterior"
          >
            <FaChevronLeft />
          </button>
          <span className="pdf-viewer-modal__pager-label">
            Página {pageNum} de {numPages}
          </span>
          <button
            type="button"
            className="pdf-viewer-modal__pager-btn"
            disabled={loading || pageNum >= numPages}
            onClick={goNext}
            aria-label="Página siguiente"
          >
            <FaChevronRight />
          </button>
        </div>
      )}

      <div className="pdf-viewer-modal__body">
        {loading && (
          <div className="pdf-viewer-modal__status">
            <FaSpinner style={{ animation: 'spin 0.8s linear infinite' }} />
            Cargando PDF…
          </div>
        )}

        {!loading && error && (
          <div className="pdf-viewer-modal__error">{error}</div>
        )}

        {!loading && !error && (
          <canvas ref={canvasRef} className="pdf-viewer-modal__canvas" />
        )}
      </div>
    </ProfaseModal>
  );
}
